import { useCallback } from 'react';
import {
  User,
  UserService,
  ParentService,
  SitterService,
} from 'us-web-services';
import { useUser, useAuthentication } from '../store';
import { addRecentUser } from '../util/recentUsersStorage';

interface UseLoadUserProfileResult {
  loadUserProfile: (user: User) => Promise<void>;
  loadUserById: (userId: number) => Promise<void>;
}

function useLoadUserProfile(): UseLoadUserProfileResult {
  const [, userActions] = useUser();
  const authenticationState = useAuthentication()[0];

  const loadUserProfile = useCallback(async (user: User) => {
    userActions.setUser(user);
    userActions.setProfile({});

    if (user.id) {
      addRecentUser({
        id: user.id,
        name: user.name,
        email: user.email,
        parent: !!user.parent,
        sitter: !!user.sitter,
        pictureUrl: user.pictureUrl,
      });

      if (user.parent) {
        const parentResponse = await ParentService.get(user.id);

        userActions.setProfile(parentResponse.data.data);
      } else if (user.sitter) {
        const sitterConfig = {
          params: {
            userId: authenticationState.authentication.user.id,
          },
        };
        const sitterResponse = await SitterService.get(user.id, sitterConfig);

        userActions.setProfile(sitterResponse.data.data);
      } else {
        userActions.setProfile({ ...user, address: {} });
      }
    }
  }, [userActions, authenticationState.authentication]);

  const loadUserById = useCallback(async (userId: number) => {
    try {
      const userConfig = { params: { fields: 'superUser' } };
      const response = await UserService.get(userId, userConfig);
      const user = response.data.data;

      await loadUserProfile(user);
    } catch {
      userActions.setUser({});
      userActions.setProfile({});
    }
  }, [loadUserProfile, userActions]);

  return { loadUserProfile, loadUserById };
}

export default useLoadUserProfile;
